import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FacebookBusinessManagerMissingWarningStep from '../Steps/FacebookBusinessManagerMissingWarningStep'
import ImpressionsStep from '../Steps/ImpressionsStep'
import BudgetStep from '../Steps/BudgetStep'
import DatesStep from '../Steps/DatesStep'
import PlacementsStep from '../Steps/PlacementsStep'
import CustomAudiencesStep from '../Steps/CustomAudiencesStep'
import PromotedObjectStep from '../Steps/PromotedObjectStep'
import ObjectiveStep from '../Steps/ObjectiveStep'
import GeotargetingStep from '../Steps/GeotargetingStep'
import LineItemStep from '../Steps/LineItemStep'

import { useManageExistingListItemFormStep } from '../../../../../../hooks/formHooks/listItemFormHooks/useManageExistingListItemFormStep'
import useRightBudgetAndImpressionsCalculation from '../../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'

import { clearLineItems } from '../../../../../../modules/actions/lineItems'
import { facebookRightUploadAdCampaignSelector, rightSelector } from '../../../../../../modules/selectors/rights'
import { selfAccountSelector } from '../../../../../../modules/selectors/selfAccounts'
import { lineItemsSelector } from '../../../../../../modules/selectors/lineItems'

import { DIGITAL_ALLOCATION_TYPE_IMPRESSIONS } from '../../../../../Multiplatform/Partnerships/RightForms/fields'
import {
  OUTCOME_APP_PROMOTION,
  OUTCOME_ENGAGEMENT,
  OUTCOME_LEADS,
  OUTCOME_SALES,
  OUTCOME_TRAFFIC
} from '../../../../../../constants/campaigns'

const useLineItemFormSteps = ({
  values,
  showPromotedObjectStepForTrafficCampaign,
  showPromotedObjectStepForAppPromotionCampaign
}) => {
  const dispatch = useDispatch()

  const campaign = useSelector(facebookRightUploadAdCampaignSelector)

  const right = useSelector(rightSelector)
  const { facebook_bm_id: facebookBmId } = useSelector(selfAccountSelector)
  const lineItems = useSelector(lineItemsSelector)

  const { allocation_type: allocationTypeOnDigitalRight } = right
  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()
  const showImpressions = allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const isCampaignBudgetEmpty = !campaign['budget_lifetime']
  const isOutcomeSalesCampaign = campaign.objective === OUTCOME_SALES
  const isOutcomeTrafficCampaign = campaign.objective === OUTCOME_TRAFFIC
  const isOutcomeLeadsCampaign = campaign.objective === OUTCOME_LEADS
  const isOutcomeAppPromotionCampaign = campaign.objective === OUTCOME_APP_PROMOTION
  const isOutcomeEngagementCampaign = campaign.objective === OUTCOME_ENGAGEMENT

  const showBudgetStep = isCampaignBudgetEmpty

  const showPromotedObjectStep = useMemo(() => {
    if (isOutcomeSalesCampaign || isOutcomeLeadsCampaign) {
      return true
    }

    if (isOutcomeAppPromotionCampaign) {
      return showPromotedObjectStepForAppPromotionCampaign
    }

    if (isOutcomeTrafficCampaign) {
      return showPromotedObjectStepForTrafficCampaign
    }

    return false
  }, [
    isOutcomeTrafficCampaign,
    isOutcomeSalesCampaign,
    isOutcomeLeadsCampaign,
    isOutcomeAppPromotionCampaign,
    showPromotedObjectStepForTrafficCampaign,
    showPromotedObjectStepForAppPromotionCampaign
  ])

  const showFacebookBusinessManagerMissingWarning = useMemo(() => {
    return isOutcomeAppPromotionCampaign && !facebookBmId
  }, [isOutcomeAppPromotionCampaign, facebookBmId])

  useEffect(
    () => () => {
      dispatch(clearLineItems())
    },
    [dispatch]
  )

  const newLineItemSteps = useMemo(
    () => [
      {
        component: <FacebookBusinessManagerMissingWarningStep />,
        show: showFacebookBusinessManagerMissingWarning
      },
      {
        component: <ObjectiveStep />,
        show: isOutcomeEngagementCampaign
      },
      {
        component: showImpressions ? (
          <ImpressionsStep remainingImpressions={remainingImpressions} />
        ) : (
          <BudgetStep remainingBudget={remainingBudget} />
        ),
        show: showBudgetStep
      },
      {
        component: <DatesStep />,
        show: true
      },
      {
        component: <GeotargetingStep />,
        show: true
      },
      {
        component: <CustomAudiencesStep />,
        show: true
      },
      {
        component: <PlacementsStep isLastStep={!showPromotedObjectStep} />,
        show: true
      },
      {
        component: <PromotedObjectStep />,
        show: showPromotedObjectStep
      }
    ],
    [
      showFacebookBusinessManagerMissingWarning,
      showImpressions,
      showBudgetStep,
      showPromotedObjectStep,
      remainingImpressions,
      remainingBudget,
      isOutcomeEngagementCampaign
    ]
  )

  return useManageExistingListItemFormStep({
    values,
    ListItemSelectionStep: <LineItemStep />,
    newListItemSteps: newLineItemSteps,
    listItems: lineItems
  })
}

export default useLineItemFormSteps
